import React, { useEffect, useState } from 'react';

import { _GetUserData } from '@netfront/gelada-react-shared';
import pluralize from 'pluralize';

import { Button } from 'components/Button/Button';
import { ActivityProps } from 'components/Dashboard/Activities/Activity';
import { Grid, GridItem } from 'components/Grid/Grid';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import UserExperienceBar from 'components/UserExperienceBar/UserExperienceBar';

import styles from './Overview.module.css';

export interface OverviewProps {
  activities?: Array<ActivityProps>;
  currentPage: string;
  lessonsCompleted: number;
  lessonsToComplete: number;
  modulesCompleted: number;
  modulesToComplete: number;
  userExperience: number;
}

interface LevelCardProps {
  currentPage: string;
  lessonsCompleted: number;
  modulesCompleted: number;
  userExperience: number;
}

const ExperienceMap = {
  0: 'Beginner',
  1: 'Novice',
  2: 'Intermediate',
  3: 'Skilled',
  4: 'Advanced',
  5: 'Seasoned',
  6: 'Expert',
  7: 'Master',
  8: 'Grand Master',
};

const LevelCard = ({ currentPage, lessonsCompleted, modulesCompleted, userExperience }: LevelCardProps) => {
  const [userLevel, setUserLevel] = useState('0');
  const [progression, setProgression] = useState(0);

  const updatedUserExperience = userExperience * 2;

  useEffect(() => {
    if (updatedUserExperience > 99) {
      setUserLevel(`${updatedUserExperience}`[0]);
    }
    setProgression(userExperience / 4);
  }, [updatedUserExperience]);

  useEffect(() => {
    if (userLevel === '8') {
      setProgression(100);
    }
  }, [userLevel]);
  return (
    <div className="l-flex l-flex--column">
      <div className="h-background--navy p-large">
        <p className="h-color--white h2 m-b--small">Level {userLevel}</p>
        <p className="h-color--white m-b--small">{ExperienceMap[userLevel]}</p>
        <div className="l-flex">
          <img alt="medal" src="/images/icon-medal.svg" />
          <UserExperienceBar progression={progression} sectionCount={10} />
        </div>
      </div>
      <div className="h-background--white p l-flex l-flex--column align-center">
        <p className="h-color--navy weight-700 m-b--xs">You have completed:</p>
        <div className="l-flex m-b--small">
          <p className="h-color--navy m-r">
            <span className="weight-700">{modulesCompleted} </span>
            {modulesCompleted === 1 ? 'Module' : 'Modules'}
          </p>
          <p className="h-color--navy">
            <span className="weight-700">{lessonsCompleted}</span> {lessonsCompleted === 1 ? 'Lesson' : 'Lessons'}
          </p>
        </div>

        <Button
          className="m-t h-background--green"
          component="link"
          link={{ href: currentPage || '/module/module-1/lesson-1-what-is-an-alcohol-problem' }}
        >
          {lessonsCompleted === 0 ? "Let's start" : 'Back to last lesson'}{' '}
          <img alt="Resume current lesson" className="m-l--small" src="/images/icon-arrow-white.svg" />
        </Button>
      </div>
    </div>
  );
};

export const Overview = ({
  activities,
  currentPage,
  lessonsCompleted,
  lessonsToComplete,
  modulesCompleted,
  modulesToComplete,
  userExperience,
}: OverviewProps): JSX.Element => {
  const toolList = activities.filter((activity) => activity.type === 'Tool');
  const unlockedTools = toolList.filter((tool) => tool.hasBeenViewed);

  const getToolLabel = (list: number, unlocked: number) => {
    const diff = toolList.length - unlockedTools.length;

    if (diff === 1) return 'tool';
    return 'tools';
  };
  return (
    <div>
      <h1 className="h-color--navy p-t">Dashboard</h1>
      <Grid>
        <GridItem className="l-flex align-center" sizes={['auto']}>
          <div className="l-flex align-center sm-column w-100">
            <div className="m-r">
              <UserAvatar />
            </div>
            <div className="w-100 cell v-align">
              <div className="w-100">
                <p className="h1 h-color--navy m-b ">Hi {_GetUserData()?.firstname},</p>
                <p className="h5 h-font--primary weight-400 m-b">
                  This is your personal dashboard. On my right you will find your progress bar, which shows you how far you have progressed
                  through the program. As you work through the program, you will be awarded different titles to recognise your growing
                  expertise, from ‘Beginner’ to ‘Grand Master’. Only those that complete all modules will be awarded the title of ‘Grand
                  Master’! Make sure to check in here regularly. It can be very rewarding to see exactly how much progress you are making.
                </p>
                <p className="h5 h-font--primary weight-400">
                  Tip: you can click the ‘back to last lesson’ button at any time to go straight back to where you last left off.
                </p>
              </div>
            </div>
          </div>
        </GridItem>
        <GridItem sizes={['sml-1', 'med-3', 'lrg-3']}>
          <LevelCard
            currentPage={currentPage}
            lessonsCompleted={lessonsCompleted}
            modulesCompleted={modulesCompleted}
            userExperience={userExperience}
          />
        </GridItem>
      </Grid>
      <h2 className="h-color--navy m-t--large">Your Progress Summary</h2>
      <p className="h5 h-font--primary weight-400 m-b--large ">
        A summary of the number of modules, lessons, activities and tools that you need to complete before reaching the end of the program.
      </p>
      <Grid className={styles['c-overview__cards']}>
        <GridItem sizes={['sml-1', 'med-2', 'lrg-3']}>
          <div className="h-background--white p-large l-flex h-100 align-start">
            <img alt="modules complete" className="m-r--large" src="/images/icon-graduationhat.svg" />
            <div>
              <p className="h1 h-color--navy m-b--small h-font--primary">{modulesToComplete}</p>
              <p className="h5 h-font--uppercase weight-800">{pluralize('Module', modulesToComplete)} to complete</p>
            </div>
          </div>
        </GridItem>

        <GridItem sizes={['sml-1', 'med-2', 'lrg-3']}>
          <div className="h-background--white p-large l-flex h-100 align-start">
            <img alt="lessons to complete" className="m-r--large" src="/images/icon-activities.svg" />
            <div>
              <p className="h1 h-color--navy m-b--small h-font--primary">{lessonsToComplete}</p>
              <p className="h5 h-font--uppercase weight-800">{pluralize('Lesson', lessonsToComplete)} to complete</p>
            </div>
          </div>
        </GridItem>

        <GridItem sizes={['sml-1', 'med-2', 'lrg-3']}>
          <div className="h-background--white p-large l-flex h-100 align-start">
            <img alt="tools" className="m-r--large" src="/images/icon-tool.svg" />
            <div>
              <div>
                <p className="h1 m-b--small h-color--navy m-r line-1 h-font--primary w-8 text-center">{unlockedTools.length}</p>
                <div>
                  <p className="h6 h-font--uppercase weight-800 h-color--navy m-b--xs">
                    Online {pluralize('tool', unlockedTools.length)} unlocked
                  </p>
                  <p className="h-font-italic">
                    {toolList.length - unlockedTools.length} online {getToolLabel(toolList.length, unlockedTools.length)} left
                  </p>
                </div>
              </div>
            </div>
          </div>
        </GridItem>
      </Grid>
    </div>
  );
};
